import { unionBy } from 'lodash';
import React, { Fragment, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';

import { TreeNode } from '../../types/TreeNode';
import { UserAction } from '../../types/UserAction';
import { Actions } from '../Actions/Actions';
import { Box } from '../Box/Box';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { Layout } from '../Layout/Layout';
import { Link } from '../Link/Link';
import { TruncateText } from '../Text/subcomponents/TruncateText';
import { Text } from '../Text/Text';
import { hoverNodes, selectNodes, toggleNodeIsExpanded } from './utils';

interface Props {
  /** Array of `TreeNode`s containing parent (and optionally children) nodes.
   * Currently only one level of nesting is supported, and `children` inside `children` nodes
   * should be passed an empty array to indicate they are leaf nodes. */
  tree: TreeNode[];
  /** Optional content to be shown when `tree` is empty. */
  emptyContent?: React.ReactNode;
  /** Optional override to add extra indentation (i.e. padding left) to child nodes.
   * By default they align horizontally with parent nodes. */
  enableExtraChildPadding?: boolean;
  /** Optional actions to be shown in the header. */
  headerActions?: UserAction[];
  /** Optional accesibility name to define the current element. */
  name?: string;
  /** Optionally provide the `key` of the node to be initially selected. */
  selectedNodeKey?: string;
  /** Tree header title. */
  title?: string;
  /** Optional override for the count shown in the header.
   * If omitted, parent node count (excluding children) is shown by default. */
  totalCount?: number;

  /** Callback for consumer to keep `treeData` in sync with `tree` prop. */
  onUpdate: (treeData: TreeNode[]) => void;
  /** callback to identify which item was toggled */
  onToggle?: (node: TreeNode) => void;
  /** When true (default), selecting a node forces it to expand. When false, respects explicit collapse. */
  expandSelectedOnSelect?: boolean;
}

export const Tree = ({
  emptyContent,
  enableExtraChildPadding = false,
  expandSelectedOnSelect = true,
  headerActions = [],
  name,
  selectedNodeKey,
  title,
  totalCount,
  tree: nodes,
  onUpdate = (_treeData: TreeNode[]) => {},
  onToggle = (_node: TreeNode) => {},
}: Props) => {
  const intl = useIntl();
  const formatNumber = useFormatNumber();
  const updateNodes = (updatedNodes: TreeNode[]) => {
    const mergedNodes = unionBy(updatedNodes, nodes, 'key');
    onUpdate(mergedNodes);
  };

  const setNodeAndChildrenSelected = (currentNode: TreeNode) => {
    const updatedNodes = selectNodes(
      nodes,
      currentNode,
      expandSelectedOnSelect,
    );
    updateNodes(updatedNodes);
  };

  const setParentAndSiblingsSelected = (currentNode: TreeNode) => {
    const parent = nodes.find((node) => node.key === currentNode.parentKey);
    setNodeAndChildrenSelected(parent as TreeNode);
  };

  const selectNode = (node: TreeNode) => {
    node.parentKey
      ? setParentAndSiblingsSelected(node)
      : setNodeAndChildrenSelected(node);
  };

  useEffect(() => {
    if (selectedNodeKey) {
      const selectedNode = nodes.find((node) => node.key === selectedNodeKey);
      if (selectedNode) {
        selectNode(selectedNode);
      }
    }
  }, [selectedNodeKey, nodes]);

  const setNodeIsExpanded = (currentNode: TreeNode) => {
    const updatedNodes = toggleNodeIsExpanded(nodes, currentNode);
    onToggle(currentNode);
    updateNodes(updatedNodes);
  };

  // TODO: break into individual components and remove render*
  const renderExpandCollapseIcon = (node: TreeNode) => (
    // TODO: create icon preset for expand/collapse
    <IconButton
      icon={node.isExpanded ? 'caret-down' : 'caret-right'}
      text={
        node.isExpanded
          ? intl.formatMessage({
              id: 'Tree.collapse',
              defaultMessage: 'Collapse',
              description: 'Collapse tree node button label',
            })
          : intl.formatMessage({
              id: 'Tree.expand',
              defaultMessage: 'Expand',
              description: 'Expand tree node button label',
            })
      }
      size="s"
      onClick={() => setNodeIsExpanded(node)}
    />
  );

  const hoverNode = (currentNode: TreeNode, isHovered: boolean) => {
    const updatedNodes = hoverNodes(nodes, currentNode, isHovered);
    updateNodes(updatedNodes);
  };

  const renderNode = (node: TreeNode) => {
    const childCount = node.children?.length ?? 0;
    const handleMouseEnter = () => {
      hoverNode(node, true);
    };
    const handleMouseLeave = () => {
      hoverNode(node, false);
    };
    return (
      <Layout
        key={node.key}
        aria-label={node.name}
        role="treeitem"
        align="center"
        justify="space-between"
        onMouseEnter={node.options?.disableHover ? undefined : handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        styles={componentStyles.row}
        styleProps={{
          childCount,
          hasContent: Boolean(node.content),
          padding: node.padding,
        }}
      >
        {node.isSelected && (
          <Box
            styles={componentStyles.selected}
            styleProps={{
              isSelected: node.isSelected,
              parentKey: node.parentKey,
            }}
          />
        )}
        <Layout
          align="center"
          spacing={1}
          styles={componentStyles.indentRow}
          styleProps={{
            childCount,
            parentKey: node.parentKey,
            options: { enableExtraChildPadding },
          }}
        >
          {Boolean(childCount) && renderExpandCollapseIcon(node)}
          {node.content ?? (
            <Box styles={componentStyles.nodeName}>
              {node.pathname ? (
                <Link
                  color="text.primary"
                  variant={node.isSelected ? 'body-medium' : 'body'}
                  pathname={node.pathname}
                  hash={node.hash}
                >
                  <TruncateText>{node.name}</TruncateText>
                </Link>
              ) : (
                <TruncateText onClick={() => node.onClick?.(node)}>
                  {node.name}
                </TruncateText>
              )}
            </Box>
          )}
        </Layout>
        <Layout align="center" spacing={1}>
          {Boolean(node.actions?.length || node.moreActions?.length) &&
            node.isHovered && (
              <Actions
                actions={node.actions ?? []}
                moreActions={node.moreActions ?? []}
              />
            )}
          {node.statusContent}
          {!node.statusContent && node.status && (
            <Box role="status" w={24} align="center">
              <Icon
                color={node.status.color}
                icon={node.status.icon}
                label={node.status.label}
                tooltip={node.status.tooltip}
              />
            </Box>
          )}
          {!node.statusContent && !node.status && !node.parentKey && (
            <Box role="status" w={24} align="center">
              <Text align="center" preset="help">
                {formatNumber(childCount)}
              </Text>
            </Box>
          )}
        </Layout>
      </Layout>
    );
  };

  const renderChildren = (children: TreeNode[]) => children.map(renderNode);

  const content = nodes.map((node, index) => (
    <Fragment key={index}>
      {renderNode(node)}
      {Boolean(node.children?.length) &&
        node.isExpanded &&
        renderChildren(node.children)}
    </Fragment>
  ));

  const header = title ? (
    <Layout
      as="header"
      align="center"
      justify="space-between"
      styles={componentStyles.header}
    >
      {/* eslint-disable-next-line jsx-a11y/aria-role -- TODO: EKP-42656 */}
      <TruncateText role="subheading" variant="body-bold" fontSize="16">
        {title}
      </TruncateText>
      <Layout align="center" spacing={1}>
        {Boolean(headerActions.length) && <Actions actions={headerActions} />}
        <Box role="status" w={24} align="center">
          <Text align="center" preset="help">
            {formatNumber(totalCount ?? nodes.length)}
          </Text>
        </Box>
      </Layout>
    </Layout>
  ) : undefined;

  return (
    <Layout aria-label={name} direction="column" styles={componentStyles.panel}>
      {header}
      {Boolean(nodes.length) ? (
        <Layout role="tree" direction="column" styles={componentStyles.content}>
          {content}
        </Layout>
      ) : (
        Boolean(emptyContent) && <>{emptyContent}</>
      )}
    </Layout>
  );
};

const componentStyles = {
  panel: {
    width: '100%',
    height: '100%',
  },
  nodeName: {
    flex: 'auto',
    minWidth: 0,
    '> *': {
      display: 'block',
      width: '100%',
    },
  },
  header: {
    height: 'header.height',
    borderBottom: 'border.divider',
    flex: 'none',
    paddingLeft: 6,
    paddingRight: 4,
  },
  content: {
    overflowY: 'auto',
    width: '100%',
  },
  row: ({
    childCount,
    hasContent,
    padding,
  }: {
    childCount?: number;
    hasContent?: boolean;
    padding?: string;
  }) => ({
    height: hasContent ? '100%' : 'height.m',
    padding: padding ?? 4,
    paddingLeft: childCount ? 4 : 6,
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    position: 'relative',
  }),
  selected: ({
    isSelected,
    parentKey,
  }: {
    isSelected?: boolean;
    parentKey?: string;
  }) => ({
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 'auto',
    width: 8,
    backgroundColor: isSelected
      ? parentKey
        ? 'status.active.secondary'
        : 'status.active'
      : '',
  }),
  indentRow: ({
    childCount,
    parentKey,
    options,
  }: {
    childCount?: number;
    parentKey?: string;
    options?: { enableExtraChildPadding: boolean };
  }) => ({
    paddingLeft:
      parentKey && options?.enableExtraChildPadding ? 12 : childCount ? 0 : 6,
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
  }),
};
